'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
  slug: string;
}

function BlogCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="w-full h-48 bg-gray-200 animate-pulse" />
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-16 bg-gray-200 rounded-full animate-pulse" />
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
        <div className="h-4 bg-gray-200 rounded animate-pulse w-4/5" />
        <div className="space-y-1.5">
          <div className="h-3 bg-gray-200 rounded animate-pulse" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-5/6" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
        </div>
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mt-2" />
      </div>
    </div>
  );
}

export default function BlogSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/blog')
      .then((res) => res.json())
      .then((data) => {
        if (data?.posts?.length > 0) {
          setPosts(data.posts.slice(0, 3));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <section
      id="blog"
      className="p-4 sm:p-6 lg:p-8 text-[#48438e] text-shadow-sm"
    >
      <div
        ref={sectionRef}
        className="mt-24 space-y-8 fade-in-section"
      >
        <div className="flex flex-col gap-1 items-center">
          <h2 className="text-4xl">Blog</h2>
          <p className="text-lg">Tulisan &amp; Artikel Terbaru</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts?.map((post) => (
              <div
                key={post?.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                {post?.image && (
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={post?.image}
                      alt={post?.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full font-medium">
                      {post?.category}
                    </span>
                    <span className="text-xs text-gray-400">{formatDate(post?.date)}</span>
                  </div>
                  <h3 className="text-base font-bold text-[#48438e] leading-snug line-clamp-2">
                    {post?.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-3">{post?.excerpt}</p>
                  <Link
                    href={`/blog/${post?.slug}`}
                    className="mt-2 inline-block text-sm font-semibold text-[#0090AD] hover:underline"
                  >
                    Baca Selengkapnya →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-center mt-4">
          <Link
            href="/blog"
            className="px-8 py-3 rounded-lg font-semibold transition hover:opacity-90 text-white bg-[#0090AD] hover:bg-[#007a93] shadow-md"
          >
            Lihat Semua Artikel
          </Link>
        </div>
      </div>
    </section>
  );
}
